"use client";

import type { FC } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";

import { useConfig } from "@jti/app-context/useConfig";
import { SoftAgeVerification as SoftAgeVerificationUI } from "@jti/ui/eva-components/SoftAgeVerification";

import { env } from "@/env";
import { useLanguages } from "@/hooks/useLanguages";
import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";
import { isModelInHardGateConfig } from "@/libs/hardGate/parseHardGateConfig";

import type { softAgeVerificationInputs } from "./SoftAgeVerification.builder";

type SoftAgeVerificationProps = BuilderComponentProps<typeof softAgeVerificationInputs> & {
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
};

export const SoftAgeVerification: FC<SoftAgeVerificationProps> = ({
  backgroundColor,
  logo,
  isLogoEnabled = true,
  shouldPageReload = false,
  isBlockingTimeframeEnabled = false,
  blockingDurationMinutes = 60,
  blockingFeedbackMessage = "",
  builderBlock,
  builderContext,
  builderComponents,
}) => {
  const languages = useLanguages();
  const { languageSelectorVariant } = useConfig();
  const isHardGateMode = isModelInHardGateConfig("soft-age-verification", env.hardGateConfig);

  const shouldDisplayModelComponent = useDisplayModelComponent("soft-age-verification");

  if (shouldDisplayModelComponent) {
    return (
      <SoftAgeVerificationUI
        languages={languages}
        isAuthorMode={isEditing()}
        logo={{ ...logo, isDarkMode: false }}
        backgroundColor={backgroundColor}
        isLogoEnabled={isLogoEnabled}
        shouldPageReload={shouldPageReload}
        isHardGateMode={isHardGateMode}
        isBlockingEnabled={isBlockingTimeframeEnabled}
        blockingMinutes={blockingDurationMinutes}
        feedbackMessage={blockingFeedbackMessage}
        variant={languageSelectorVariant?.softAgeVerification?.variant}
      >
        {builderBlock?.children?.map((child) => (
          <Blocks
            key={child.id}
            blocks={[child]}
            parent={builderBlock?.id}
            path="children"
            context={builderContext}
            registeredComponents={builderComponents}
          />
        ))}
      </SoftAgeVerificationUI>
    );
  }

  return null;
};
